import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSocket } from "../context/SocketContext";

// ---- Types ----

interface BarkobaState {
  phase: "GUESSING" | "TEAM_SELECTION" | "COMPLETED" | null;
  roundId: string | null;
  wordLength: number;
  hints: Record<number, string>;
  rewardAmount: number;
  maxTeammates: number | null;
  winnerTwitchName: string | null;
  teamSelectionEndsAt: string | null;
  teammates: string[];
  secretWord: string | null;
}

interface GuessEntry {
  username: string;
  count: number;
  max: number;
}

const INITIAL_STATE: BarkobaState = {
  phase: null,
  roundId: null,
  wordLength: 0,
  hints: {},
  rewardAmount: 0,
  maxTeammates: null,
  winnerTwitchName: null,
  teamSelectionEndsAt: null,
  teammates: [],
  secretWord: null,
};

const MAX_GUESS_ROWS = 30;
const COMPLETED_DISPLAY_MS = 8000;

// ---- Neon palette per phase ----

const NEON = {
  GUESSING: {
    color: "#00e5ff",
    glow: "rgba(0,229,255,0.55)",
    dim: "rgba(0,229,255,0.12)",
    text: "#67eeff",
  },
  TEAM_SELECTION: {
    color: "#ffe600",
    glow: "rgba(255,230,0,0.55)",
    dim: "rgba(255,230,0,0.12)",
    text: "#ffe978",
  },
  COMPLETED: {
    color: "#39ff14",
    glow: "rgba(57,255,20,0.55)",
    dim: "rgba(57,255,20,0.12)",
    text: "#7fff5f",
  },
};

// ---- Countdown hook ----

function useCountdown(endsAt: string | null): number {
  const [secs, setSecs] = useState(30);
  useEffect(() => {
    if (!endsAt) {
      setSecs(30);
      return;
    }
    const update = () =>
      setSecs(
        Math.max(
          0,
          Math.floor((new Date(endsAt).getTime() - Date.now()) / 1000),
        ),
      );
    update();
    const id = setInterval(update, 200);
    return () => clearInterval(id);
  }, [endsAt]);
  return secs;
}

// ---- Timer bar color (green → amber → red) ----

function timerBarColor(secs: number, total = 30): string {
  const pct = secs / total;
  if (pct > 0.5) return "#22c55e";
  if (pct > 0.2) return "#f59e0b";
  return "#ef4444";
}

// ---- Word display ----

function WordDisplay({
  wordLength,
  hints,
  neonColor,
}: {
  wordLength: number;
  hints: Record<number, string>;
  neonColor: string;
}) {
  return (
    <div className="flex flex-wrap gap-2 justify-center px-2">
      {Array.from({ length: wordLength }).map((_, i) => {
        const letter = hints[i];
        return (
          <motion.div
            key={i}
            className="flex items-end justify-center w-8 h-10 text-xl font-black border-b-2 rounded-md"
            style={{
              borderColor: letter ? "#39ff14" : `${neonColor}99`,
              color: letter ? "#7fff5f" : "rgba(255,255,255,0.45)",
              textShadow: letter
                ? "0 0 10px #39ff14, 0 0 20px #39ff14"
                : "none",
              boxShadow: letter ? `0 4px 12px rgba(57,255,20,0.5), inset 0 0 8px rgba(57,255,20,0.15)` : "none",
            }}
            animate={letter ? { scale: [1, 1.4, 1] } : { scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            {letter ?? "_"}
          </motion.div>
        );
      })}
    </div>
  );
}

// ---- Main overlay ----

export function BarkobaOverlay() {
  const { socket } = useSocket();
  const [game, setGame] = useState<BarkobaState>(INITIAL_STATE);
  const [visible, setVisible] = useState(false);
  const [guesses, setGuesses] = useState<GuessEntry[]>([]);
  const gamePhaseRef = useRef<string | null>(null);
  const completedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const secs = useCountdown(game.teamSelectionEndsAt);

  useEffect(() => {
    gamePhaseRef.current = game.phase;
  }, [game.phase]);

  // Megosztott alkalmazó — ugyanazt a payload alakot (GET /barkoba/overlay
  // válasza) dolgozza fel, akár REST hívásból (első betöltés), akár a
  // socket "overlay:hydrate" eventből (minden connect/reconnect) érkezik.
  const applyRound = (round: any) => {
    if (!round) return;
    setGame({
      phase: round.status,
      roundId: round.roundId,
      wordLength: round.wordLength ?? 0,
      hints: {},
      rewardAmount: round.rewardAmount,
      maxTeammates: round.maxTeammates,
      winnerTwitchName: round.winnerTwitchName,
      teamSelectionEndsAt: round.teamSelectionEndsAt,
      teammates: round.teammates ?? [],
      secretWord: round.secretWord,
    });
    if (round.status === "GUESSING" || round.status === "TEAM_SELECTION") {
      setVisible(true);
    } else if (round.status === "COMPLETED") {
      setVisible(true);
      if (completedTimerRef.current) clearTimeout(completedTimerRef.current);
      completedTimerRef.current = setTimeout(() => {
        setVisible(false);
        setTimeout(() => setGame(INITIAL_STATE), 1000);
      }, COMPLETED_DISPLAY_MS);
    }
  };

  // ---- Fetch initial state on mount ----
  useEffect(() => {
    fetch("https://api.palinagyonbuzi.com/api/barkoba/overlay")
      .then((r) => r.json())
      .then((d) => applyRound(d.round))
      .catch(() => {});
  }, []);

  // Socket-alapú hydration — MINDEN connection/reconnect eseménynél lefut
  // (server.js io.on("connection", ...)), a fenti REST hívás pedig csak
  // az első mountnál. Enélkül egy OBS browser source frissítés / hálózati
  // megszakadás közben futó barkoba kör elveszne az overlay-en.
  useEffect(() => {
    if (!socket) return;

    const handleHydrate = (data: { barkoba?: { round: any } }) => {
      if (data.barkoba) applyRound(data.barkoba.round);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (socket as any).on("overlay:hydrate", handleHydrate);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (socket as any).off("overlay:hydrate", handleHydrate);
    };
  }, [socket]);

  // ---- Overlay socket events ----
  useEffect(() => {
    if (!socket) return;

    const onOverlay = (msg: { type: string; data: any }) => {
      const { type, data } = msg;

      if (type === "barkoba:started") {
        if (completedTimerRef.current) {
          clearTimeout(completedTimerRef.current);
          completedTimerRef.current = null;
        }
        setGame({
          phase: "GUESSING",
          roundId: data.roundId,
          wordLength: data.wordLength ?? 0,
          hints: {},
          rewardAmount: data.rewardAmount,
          maxTeammates: data.maxTeammates,
          winnerTwitchName: null,
          teamSelectionEndsAt: null,
          teammates: [],
          secretWord: null,
        });
        setGuesses([]);
        setVisible(true);
      } else if (type === "barkoba:guess") {
        setGuesses((prev) => {
          const idx = prev.findIndex((g) => g.username === data.twitchName);
          if (idx !== -1) {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], count: data.guessCount };
            return updated;
          }
          const entry: GuessEntry = {
            username: data.twitchName,
            count: data.guessCount,
            max: data.maxGuessPerUser,
          };
          return [...prev.slice(-(MAX_GUESS_ROWS - 1)), entry];
        });
      } else if (type === "barkoba:hint") {
        setGame((prev) => ({
          ...prev,
          hints: { ...prev.hints, [data.position]: data.letter },
        }));
      } else if (type === "barkoba:winner-found") {
        setGame((prev) => ({
          ...prev,
          phase: "TEAM_SELECTION",
          winnerTwitchName: data.winnerTwitchName,
          teamSelectionEndsAt: data.teamSelectionEndsAt,
          teammates: data.teammates ?? [],
          rewardAmount: data.rewardAmount,
          maxTeammates: data.maxTeammates,
        }));
        setGuesses([]);
      } else if (type === "barkoba:teammate-added") {
        setGame((prev) => ({
          ...prev,
          teammates: data.teammates ?? prev.teammates,
        }));
      } else if (type === "barkoba:completed") {
        setGame((prev) => ({
          ...prev,
          phase: "COMPLETED",
          secretWord: data.secretWord,
          winnerTwitchName: data.winnerTwitchName,
          teammates: data.teammates ?? prev.teammates,
        }));
        setGuesses([]);
        if (completedTimerRef.current) clearTimeout(completedTimerRef.current);
        completedTimerRef.current = setTimeout(() => {
          setVisible(false);
          setTimeout(() => setGame(INITIAL_STATE), 1000);
        }, COMPLETED_DISPLAY_MS);
      } else if (type === "barkoba:cancelled") {
        if (completedTimerRef.current) {
          clearTimeout(completedTimerRef.current);
          completedTimerRef.current = null;
        }
        setVisible(false);
        setTimeout(() => {
          setGame(INITIAL_STATE);
          setGuesses([]);
        }, 900);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (socket as any).on("overlay", onOverlay);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (socket as any).off("overlay", onOverlay);
    };
  }, [socket]);


  useEffect(() => {
    return () => {
      if (completedTimerRef.current) clearTimeout(completedTimerRef.current);
    };
  }, []);

  const neon = NEON[game.phase as keyof typeof NEON] ?? NEON.GUESSING;
  const barColor = timerBarColor(secs);
  const timerPct = Math.max(0, Math.min(1, secs / 30));
  const timerGlow = timerBarColor(secs);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="absolute inset-0 z-50 rounded-3xl overflow-hidden flex flex-col"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{
            opacity: 0,
            x: "115%",
            scale: 0.98,
            rotate: 2,
            filter: "blur(8px)",
            transition: { duration: 0.85, ease: [0.76, 0, 0.24, 1] },
          }}
          transition={{ duration: 0.5 }}
          style={{
            background:
              "linear-gradient(160deg, rgba(4,6,16,0.98) 0%, rgba(8,10,24,0.98) 100%)",
            border: `1px solid ${neon.color}`,
            boxShadow: `0 0 24px ${neon.glow}, 0 0 60px ${neon.dim}, inset 0 0 30px ${neon.dim}`,
            backdropFilter: "blur(12px)",
          }}
        >
          {/* ======= GUESSING ======= */}
          {game.phase === "GUESSING" && (
            <div
              className="flex flex-col h-full pb-1"
              style={{
                paddingTop: "14px",
                paddingLeft: "14px",
                paddingRight: "14px",
              }}
            >
              {/* Header */}
              <div className="text-center flex-shrink-0 mb-2">
                <p
                  className="text-xs uppercase tracking-[0.2em] font-black"
                  style={{
                    color: neon.text,
                    textShadow: `0 0 10px ${neon.glow}`,
                  }}
                >
                  Barkoba
                </p>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: "rgba(255,255,255,0.35)" }}
                >
                  {game.rewardAmount} esponts
                  {game.maxTeammates !== null
                    ? ` · max ${game.maxTeammates} winners`
                    : ""}
                </p>
              </div>

              {/* Word underscores */}
              {game.wordLength > 0 && (
                <div className="flex-shrink-0 py-2">
                  <WordDisplay
                    wordLength={game.wordLength}
                    hints={game.hints}
                    neonColor={neon.color}
                  />
                </div>
              )}

              {/* How-to-guess hint */}
              <div className="flex-shrink-0 text-center mb-1">
                <p
                  className="text-[11px] leading-tight"
                  style={{ color: "rgba(255,255,255,0.4)" }}
                >
                  To guess, add a{" "}
                  <span
                    className="font-black"
                    style={{
                      color: neon.color,
                      textShadow: `0 0 6px ${neon.glow}`,
                    }}
                  >
                    !
                  </span>{" "}
                  right after the word - E.g.{" "}
                  <span className="font-mono font-bold text-white/70">
                    apple!
                  </span>
                </p>
              </div>

              {/* Guess tracker */}
              <div className="flex-1 overflow-hidden min-h-0 mt-2">
                <div className="grid gap-1" style={{ gridTemplateColumns: guesses.length > 20 ? "1fr 1fr 1fr" : guesses.length > 10 ? "1fr 1fr" : "1fr" }}>
                  <AnimatePresence initial={false}>
                    {guesses.map((entry) => {
                      const maxed = entry.count >= entry.max;
                      const countColor = maxed ? "#ef4444" : "#ffe600";
                      const countGlow = maxed ? "rgba(239,68,68,0.6)" : "rgba(255,230,0,0.6)";
                      return (
                        <motion.div
                          key={entry.username}
                          className="flex items-center justify-between px-3 py-1.5 rounded-lg"
                          style={{
                            background: neon.dim,
                            border: `1px solid ${neon.color}25`,
                          }}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.12 }}
                        >
                          <span
                            className="text-sm font-bold truncate"
                            style={{ color: "rgba(255,255,255,0.9)" }}
                          >
                            {entry.username}
                          </span>
                          <span
                            className="text-sm font-black font-mono tabular-nums flex-shrink-0 ml-2"
                            style={{
                              color: countColor,
                              textShadow: `0 0 8px ${countGlow}`,
                            }}
                          >
                            {entry.count}/{entry.max}
                          </span>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          )}

          {/* ======= TEAM_SELECTION ======= */}
          {game.phase === "TEAM_SELECTION" && (
            <div
              className="flex flex-col h-full pb-3"
              style={{
                paddingTop: "14px",
                paddingLeft: "14px",
                paddingRight: "14px",
              }}
            >
              {/* Header */}
              <div className="text-center flex-shrink-0 mb-2">
                <p
                  className="text-xs uppercase tracking-[0.2em] font-black"
                  style={{
                    color: neon.text,
                    textShadow: `0 0 10px ${neon.glow}`,
                  }}
                >
                  Barkoba · Winner!
                </p>
              </div>

              {/* Winner */}
              <motion.div
                className="text-center flex-shrink-0 mb-2"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <p
                  className="text-3xl font-black leading-tight"
                  style={{
                    color: neon.text,
                    textShadow: `0 0 14px ${neon.glow}, 0 0 30px ${neon.glow}`,
                  }}
                >
                  {game.winnerTwitchName}
                </p>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: "rgba(255,255,255,0.4)" }}
                >
                  {game.rewardAmount} esponts each
                </p>
              </motion.div>

              {/* Teammates */}
              <div className="flex-1 flex flex-col gap-1.5 overflow-hidden min-h-0">
                {game.maxTeammates !== null && (
                  <p
                    className="text-xs text-center flex-shrink-0"
                    style={{ color: "rgba(255,255,255,0.3)" }}
                  >
                    You have {secs}s to choose up to {game.maxTeammates} winner
                    {game.maxTeammates !== 1 ? "s" : ""}
                  </p>
                )}
                <div className="flex flex-col gap-1 overflow-hidden">
                  <AnimatePresence initial={false}>
                    {game.teammates.map((name, i) => (
                      <motion.div
                        key={name}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-xl"
                        style={{
                          background: neon.dim,
                          border: `1px solid ${neon.color}40`,
                        }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04, duration: 0.22 }}
                      >
                        <span
                          className="text-xs font-black w-5 text-right flex-shrink-0"
                          style={{ color: `${neon.color}70` }}
                        >
                          #{i + 1}
                        </span>
                        <span className="text-sm font-bold text-white/85 truncate">
                          {name}
                        </span>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>

              {/* Timer seconds label — only show when NOT limited (limited case shows secs inline above) */}
              {game.maxTeammates === null && (
                <div className="flex justify-between items-center mt-2 mb-1 flex-shrink-0">
                  <span
                    className="text-xs"
                    style={{ color: "rgba(255,255,255,0.3)" }}
                  >
                    Team selection
                  </span>
                  <span
                    className="text-xs font-black font-mono"
                    style={{
                      color: timerGlow,
                      textShadow: `0 0 8px ${timerGlow}`,
                    }}
                  >
                    {secs}s
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ======= COMPLETED ======= */}
          {game.phase === "COMPLETED" && (
            <div
              className="flex flex-col h-full items-center justify-center text-center"
              style={{ padding: "14px" }}
            >
              <motion.p
                className="text-xs uppercase tracking-[0.2em] font-black mb-3"
                style={{
                  color: neon.text,
                  textShadow: `0 0 10px ${neon.glow}`,
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                Barkoba · Solved!
              </motion.p>

              {game.secretWord && (
                <motion.div
                  className="mb-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <p
                    className="text-xs mb-1"
                    style={{ color: "rgba(255,255,255,0.35)" }}
                  >
                    The word
                  </p>
                  <p
                    className="text-4xl font-black font-mono tracking-wide"
                    style={{
                      color: neon.text,
                      textShadow: `0 0 14px ${neon.glow}, 0 0 30px ${neon.glow}`,
                    }}
                  >
                    {game.secretWord}
                  </p>
                </motion.div>
              )}

              <motion.div
                className="mb-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <p
                  className="text-xs mb-0.5"
                  style={{ color: "rgba(255,255,255,0.35)" }}
                >
                  Winner
                </p>
                <p
                  className="text-2xl font-black"
                  style={{
                    color: NEON.TEAM_SELECTION.text,
                    textShadow: `0 0 12px ${NEON.TEAM_SELECTION.glow}`,
                  }}
                >
                  {game.winnerTwitchName}
                </p>
              </motion.div>

              {game.teammates.length > 0 && (
                <motion.div
                  className="flex flex-col gap-0.5 w-full mb-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <p
                    className="text-xs mb-1"
                    style={{ color: "rgba(255,255,255,0.3)" }}
                  >
                    Team
                  </p>
                  {game.teammates.map((name) => (
                    <div
                      key={name}
                      className="text-sm font-semibold"
                      style={{ color: "rgba(255,255,255,0.65)" }}
                    >
                      {name}
                    </div>
                  ))}
                </motion.div>
              )}

              <motion.p
                className="text-sm"
                style={{ color: "rgba(255,255,255,0.4)" }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                Everyone received{" "}
                <span
                  className="font-black"
                  style={{
                    color: neon.text,
                    textShadow: `0 0 8px ${neon.glow}`,
                  }}
                >
                  {game.rewardAmount}
                </span>{" "}
                points
              </motion.p>
            </div>
          )}

          {/* ======= TIMER BAR (bottom, TEAM_SELECTION only) ======= */}
          {game.phase === "TEAM_SELECTION" && (
            <div
              className="absolute bottom-0 left-0 right-0 h-1.5 rounded-b-3xl overflow-hidden"
              style={{ background: "rgba(255,255,255,0.06)" }}
            >
              <motion.div
                className="h-full"
                style={{
                  background: barColor,
                  boxShadow: `0 0 10px ${barColor}, 0 0 20px ${barColor}`,
                }}
                animate={{ width: `${timerPct * 100}%` }}
                transition={{ duration: 0.2, ease: "linear" }}
              />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
